import React from "react";
import { Card, CardContent, Typography, Box, Badge } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { LocalShipping, CalendarToday, ArrowRight, Cancel, AttachFile } from "@mui/icons-material";
import { TransportOrderListItemDto, TransportOrderState } from "../../../models/transport/transportOrder";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Base } from "../../../framework/common/base";
import { OrderStateToggle } from "./orderStateToggle";
import { useAppDispatch } from "../../../framework/redux/hooks";
import { saveTransportOrderWithNotification } from "../../../framework/redux/slices/transportOrderDetailsSlice";
import { HandleSaveProps, toUpdateDto } from "./transportOrderUtils";
import { addErrorMsg, addSuccessMsg } from "../../../framework/redux/slices/appSlice";

interface OrderCardProps {
    transportOrder: TransportOrderListItemDto;
    fetchTransportOrders?: () => void;
    showOrderStateToggle?: boolean;
}

export const OrderCard = ({ transportOrder, fetchTransportOrders, showOrderStateToggle }: OrderCardProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const editDisabledState = transportOrder?.state >= TransportOrderState.Cancelled;

    const handleSave: HandleSaveProps = async (
        updatedFields = {},
        location
    ) => {
        try {
            if (!transportOrder) return;
            const orderToSave = toUpdateDto(transportOrder, updatedFields, location);
            await dispatch(
                saveTransportOrderWithNotification({
                    updateDto: orderToSave,
                    orderId: transportOrder.id,
                })
            ).unwrap();
            dispatch(addSuccessMsg(t("transport.order.saveSuccess")));
            fetchTransportOrders();
        } catch (error) {
            dispatch(addErrorMsg({ message: t("transport.order.saveFailed") }));
        }
    };

    const handleStateChange = (newState, location) => {
        void handleSave({ state: newState }, location);
    };

    const getScheduledDateTime = (startTime?: string, endTime?: string) => {
        if (startTime && endTime) {
            return Base.capitalizeFirstChar(Base.dateToDayDateTimeStr(startTime)) + " - " +
                Base.capitalizeFirstChar(Base.dateToDayDateTimeStr(endTime));
        }
        if (startTime) {
            return Base.capitalizeFirstChar(Base.dateToDayDateTimeStr(startTime));
        }
        return " - " + Base.capitalizeFirstChar(Base.dateToDayDateTimeStr(endTime));
    };

    return (
        <Card sx={{ backgroundColor: "#FFF0DA", borderRadius: 2, m: 2, mb: 2 }}>
            <CardContent sx={{
                "&:last-child": { pb: 2 }
            }}
            >
                <Grid2 container spacing={2} alignItems="center">
                    <Grid2 xs={12} >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                                mb: 1,
                            }}
                        >
                            <Box
                                onClick={() => navigate(`/transport/order/${transportOrder.id}`)}
                                sx={{ cursor: "pointer" }}
                                display="flex"
                                alignItems="center"
                            >
                                <Typography variant="h5" sx={{ color: "primary.dark" }}>
                                    {transportOrder.orderNumber} {transportOrder.name}
                                </Typography>
                                <ArrowRight sx={{ color: "primary.dark" }} />
                            </Box>
                            <Box sx={{ mr: 1, mt: 1 }}>
                                {transportOrder?.attachments?.length > 0 ? (
                                    <Badge
                                        badgeContent={transportOrder?.attachments?.length}
                                        color="primary"
                                    >
                                        <AttachFile color="primary" />
                                    </Badge>
                                ) : (
                                    <AttachFile color="gray" />
                                )}
                            </Box>
                        </Box>
                    </Grid2>
                    {(transportOrder?.scheduledStartDateTime || transportOrder?.scheduledEndDateTime) &&
                        <Grid2 xs={12} display="flex" alignItems="center">
                            <CalendarToday sx={{ color: "primary.dark" }}/>
                            <Typography sx={{ color: "primary.dark", ml: 1 }} variant="h5">
                                {getScheduledDateTime(transportOrder?.scheduledStartDateTime, transportOrder?.scheduledEndDateTime)}
                            </Typography>
                        </Grid2>
                    }
                    {transportOrder.state === TransportOrderState.Cancelled && (
                        <Grid2 xs={12} display="flex" alignItems="center">
                            <Cancel sx={{ color: "red.main" }}/>
                            <Typography variant="subtitle2" sx={{ color: "red.main", ml: 1 }}>
                                {t("transport.order.orderCancelled")}
                            </Typography>
                        </Grid2>
                    )}
                    <Grid2 xs={12} display="flex" alignItems="center">
                        <LocalShipping sx={{ color: "primary.dark" }}/>
                        {transportOrder.vehicleName ? (
                            <Typography variant="subtitle2" sx={{ color: "primary.dark", ml: 1 }}>
                                {transportOrder.vehicleName}
                            </Typography>
                        ) : (
                            <Typography sx={{ color: "text.secondary", fontStyle: "italic" }} ml={1}>
                                {t("fleet.noVehicle")}
                            </Typography>
                        )}
                    </Grid2>
                    {transportOrder.senderName && (
                        <Grid2 xs={12} display="flex" alignItems="center">
                            <Typography variant="subtitle2" sx={{ color: "primary.dark" }}>
                                {t("transport.order.sender")}: {transportOrder.senderName}
                            </Typography>
                        </Grid2>
                    )}
                    {transportOrder.receiverName && (
                        <Grid2 xs={12} display="flex" alignItems="center">
                            <Typography variant="subtitle2" sx={{ color: "primary.dark" }}>
                                {t("transport.order.receiver")}: {transportOrder.receiverName}
                            </Typography>
                        </Grid2>
                    )}
                    {showOrderStateToggle && (
                        <Grid2 xs={12}>
                            <OrderStateToggle
                                order={transportOrder}
                                onStateChange={handleStateChange}
                                disabled={editDisabledState}
                            />
                        </Grid2>
                    )}
                </Grid2>
            </CardContent>
        </Card>
    );
};
